import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';

const ImageUploadModal = ({ onInsert, onClose }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadStatus('uploading');

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setPreview(imageUrl);
      setImageUrl(imageUrl);
      setUploadStatus('success');
    };
    reader.readAsDataURL(file);
  };

  const handleInsert = () => {
    if (imageUrl || preview) {
      onInsert(imageUrl || preview);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-primary flex items-center gap-2">
            <ImageIcon className="w-6 h-6" />
            Insert Image
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Drag & Drop Zone */}
        {!preview && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all mb-4 ${
              isDragging 
                ? 'border-primary bg-blue-50' 
                : 'border-gray-300 hover:border-primary hover:bg-gray-50'
            }`}
          >
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-primary' : 'text-gray-400'}`} />
            <p className="text-gray-700 font-semibold mb-2">
              {isDragging ? 'Drop image here' : 'Drag & drop image here'}
            </p>
            <p className="text-sm text-gray-500 mb-4">or</p>
            <button
              type="button"
              className="bg-primary hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors font-semibold"
            >
              Browse Files
            </button>
            <p className="text-xs text-gray-500 mt-4">
              Supported: JPG, PNG, GIF (Max 5MB)
            </p>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {/* Upload Status */}
        {uploadStatus === 'uploading' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="text-blue-800 font-semibold">Uploading image...</span>
            </div>
          </div>
        )}

        {/* Image Preview */}
        {preview && (
          <div className="mb-4">
            <img 
              src={preview} 
              alt="Preview" 
              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
            />
            <button
              type="button"
              onClick={() => {
                setPreview('');
                setImageUrl('');
                setUploadStatus('');
              }}
              className="mt-2 text-red-600 hover:text-red-800 text-sm font-semibold"
            >
              Remove Image
            </button>
          </div>
        )}

        {/* Manual URL Input */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <LinkIcon className="w-4 h-4" />
            Or enter image URL:
          </label>
          <input
            type="text"
            value={imageUrl}
            onChange={(e) => {
              setImageUrl(e.target.value);
              setPreview(e.target.value);
            }}
            placeholder="https://example.com/image.jpg"
            className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleInsert}
            disabled={!preview && !imageUrl}
            className="flex-1 bg-primary hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Insert Image
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;
